import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import SweetAlert from 'react-bootstrap-sweetalert';

import styles from './AddToCartSection.css';
import { AppLink, routeNaming } from '../../routes';
import { AddToCartButton } from './add-to-cart-button';
import iconInfo from './icon-info.svg';
import { Checkbox } from '../checkbox';
import { CartStore } from '../../stores/CartStore';
import { AlertZone } from '../alert-zone';
import { InfoModal } from '../info-modal';
import { constants } from '../../config';
import { GA4 } from '../../helpers/GA4';

const IPropTypes = {
  className: PropTypes.string,
  buttonText: PropTypes.string,
  product: PropTypes.shape({
    ableToSell: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    isService: PropTypes.bool,
    serviceMessage: PropTypes.string,
    serviceProduct: PropTypes.shape({
      id: PropTypes.string.isRequired,
      financing: PropTypes.shape({
        priceString: PropTypes.string.isRequired,
      }).isRequired,
    }),
    stockIsEmpty: PropTypes.func.isRequired,
    stockIsNearlyEmpty: PropTypes.func.isRequired,
    stockIsAlmostNone: PropTypes.func.isRequired,
    notAbleToSellString: PropTypes.string,
    inUse: PropTypes.bool,
  }).isRequired,
  showText: PropTypes.bool,
};

const defaultProps = {
  className: '',
  buttonText: undefined,
  showText: true,
};

class AddToCartSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      installationChecked: false,
      promptInstallation: false,
    };

    this._onAddToCart = this._onAddToCart.bind(this);
    this._toggleCheckbox = this._toggleCheckbox.bind(this);
    this._onConfirmInstallation = this._onConfirmInstallation.bind(this);
    this._onConfirmInstallationAccepted = this._onConfirmInstallationAccepted.bind(this);
  }

  _toggleCheckbox() {
    this.setState(prevState => ({
      installationChecked: !prevState.installationChecked,
    }));
  }

  _onConfirmInstallation() {
    // eslint-disable-next-line react/prop-types
    const { id, name, financing: { price } } = this.props.product;
    GA4.addToCartEvent(price, name, id, 1);
    if (this.props.product.serviceProduct && !this.props.showText) {
      this.setState({
        promptInstallation: true,
      });
    } else {
      this._onAddToCart();
    }
  }

  _onConfirmInstallationAccepted() {
    this.setState({
      installationChecked: true,
    }, this._onAddToCart);
  }

  _onAddToCart() {
    const currentCartItem = CartStore.cart &&
      CartStore.cart.find(item => item.product.id === this.props.product.id);
    const productIsInCart = !!currentCartItem;
    let serviceAmount = productIsInCart ? currentCartItem.serviceAmount : 0;
    if (this.state.installationChecked && this.props.product.serviceProduct) {
      serviceAmount += 1;
    }
    this.setState({
      promptInstallation: false,
      installationChecked: false,
    });
    if (productIsInCart) {
      CartStore.changeCartProductAmount(
        this.props.product.id,
        currentCartItem.amount + 1,
        serviceAmount,
      );
    } else {
      CartStore.addToCart(this.props.product, 1, serviceAmount);
    }
  }

  _renderStock() {
    if (this.props.product.stockIsEmpty()) {
      // eslint-disable-next-line react/prop-types
      const piece = this.props.product.notAbleToSellString && this.props.product.notAbleToSellString.split(' disponible ');
      let message = `Sin stock. Fecha estimada: ${piece && piece.length > 1 ? piece[1] : '-'}.`;
      // eslint-disable-next-line react/prop-types
      if (this.props.product && !this.props.product.shouldShowPrice()) {
        message += 'Fijado el precio se publicará inmediatamente.';
      }
      return (
        <p className={styles.noStockMessage}>
          {message}
        </p>);
    }
    if (this.props.product.stockIsAlmostNone()) {
      return <p className={styles.stockText}> No hay, o hay muy poco </p>;
    }
    if (this.props.product.stockIsNearlyEmpty()) {
      return <p className={styles.stockText}> Stock disponible, últimas unidades </p>;
    }
    return <p className={styles.stockText}> Stock disponible </p>;
  }

  render() {
    const currentCartItem = CartStore.cart &&
      CartStore.cart.find(item => item.product.id === this.props.product.id);
    const productIsInCart = !!currentCartItem;
    const productIsAtMaxAmount = productIsInCart &&
      currentCartItem.amount === currentCartItem.product.maxAmountToSell;
    const disabledToAddCart = !this.props.product.ableToSell || this.props.product.stockIsEmpty() ||
      this.props.product.stockIsAlmostNone() || this.props.product.isService;
    return (
      <div className={this.props.className}>
        {
          !!this.props.product.serviceProduct && this.props.showText &&
          <Fragment>
            <div className={styles.installationContainer}>
              <Checkbox
                checked={this.state.installationChecked}
                className={styles.checkbox}
                onClick={this._toggleCheckbox}
              />
              Incluir instalación de equipo -
              &nbsp;${this.props.product.serviceProduct.financing.priceString}
              <AppLink
                className={styles.infoIcon}
                routeName={routeNaming.PRODUCT_DETAIL}
                pathParams={{ id: this.props.product.serviceProduct.id }}
              >
                <img alt="" src={iconInfo} />
              </AppLink>
            </div>
            <InfoModal
              id="servicio-instalación-covid"
              type="component"
              showConfirm={false}
              textCancel="Cerrar"
              showModal
            >
              <AlertZone
                show={constants.activeCovid}
                title=""
                subtitle="Medidas sanitarias durante la instalación"
              />
            </InfoModal>
          </Fragment>
          }
        <AddToCartButton
          className={styles.addToCartButton}
          disabled={disabledToAddCart || productIsAtMaxAmount}
          onClick={this._onConfirmInstallation}
          buttonText={this.props.buttonText}
        />
        {
          productIsAtMaxAmount && this.props.showText &&
          <p className={styles.genericSubtext}>
            No puede agregar más unidades de este producto al carrito.
          </p>
        }
        {
          this.props.showText && this._renderStock()
        }
        {
          !this.props.product.inUse && this.props.showText &&
          <p className={styles.productInUseText}> Fuera de uso </p>
        }
        {
          this.props.showText && !this.props.product.stockIsEmpty() &&
          <p className={styles.genericSubtext}>
            Medios de pago: pago electrónico, pago contra entrega o en redes de cobranza.
          </p>
        }
        {
          this.state.promptInstallation &&
          <SweetAlert
            title="¿Desea incluir el servicio de instalación del equipo?"
            showCancel
            confirmBtnText="Sí, quiero"
            confirmBtnCssClass={`${styles.btnAccept} ${styles.btn}`}
            cancelBtnText="No, yo lo instalo"
            cancelBtnCssClass={`${styles.btnNoAccept} ${styles.btn}`}
            placeHolder="Write something"
            onConfirm={this._onConfirmInstallationAccepted}
            onCancel={this._onAddToCart}
            closeOnClickOutside={false}
            style={{
              width: '100%',
              left: 0,
              right: 0,
              maxWidth: '500px',
              margin: '0 auto',
              top: '30%',
            }}
          >
            <p>
              La instalación del equipo tiene un costo de
              ${this.props.product.serviceProduct.financing.priceString}
            </p>
          </SweetAlert>
        }
      </div>
    );
  }
}
AddToCartSection.propTypes = IPropTypes;
AddToCartSection.defaultProps = defaultProps;


const ObservedAddToCartSection = observer(AddToCartSection);

export { ObservedAddToCartSection as AddToCartSection };



// WEBPACK FOOTER //
// ./src/common/add-to-cart-section/AddToCartSection.jsx